import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import TopBar from "@/components/TopBar";
import VerifiedOrgBadge from "@/components/VerifiedOrgBadge";
import { BRANCH_META } from "@/lib/branches";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, MailOpen, Send, Users, Sparkles, Briefcase, Building2, ArrowRight, RefreshCw } from "lucide-react";

export default function Inbox() {
    const [tab, setTab] = useState("inbox");
    const [inbox, setInbox] = useState(null);
    const [sent, setSent] = useState(null);
    const [talent, setTalent] = useState(null);
    const [talentBusy, setTalentBusy] = useState(false);

    const load = async () => {
        const [inb, snt] = await Promise.all([
            api.get("/notes/inbox"),
            api.get("/notes/sent"),
        ]);
        setInbox(inb.data);
        setSent(snt.data);
    };

    const loadTalent = async (force = false) => {
        setTalentBusy(true);
        try {
            const { data } = await api.get(`/inbox/talent-matches${force ? "?force=true" : ""}`);
            setTalent(data);
        } catch {
            setTalent({ matches: [], error: true });
        } finally {
            setTalentBusy(false);
        }
    };

    useEffect(() => {
        load();
        loadTalent(false);
    }, []);

    const markRead = async (id) => {
        try {
            await api.post(`/notes/${id}/read`);
            setInbox((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
        } catch {
            toast.error("Couldn't mark as read.");
        }
    };

    const notes = tab === "inbox" ? inbox : sent;
    const unread = (inbox || []).filter((n) => !n.read).length;

    return (
        <div className="min-h-screen w-full">
            <TopBar />
            <div className="max-w-3xl mx-auto pt-24 pb-16 px-4 sm:px-8">
                <p className="chip" style={{ background: BRANCH_META.humanities.soft, color: BRANCH_META.humanities.text }}>
                    <Mail className="w-3 h-3" /> Peer notes
                </p>
                <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-slate-900 mt-2 leading-none">
                    Your inbox.
                </h1>
                <p className="text-slate-600 mt-2 max-w-2xl">
                    Direct notes from peers who share your disciplines. Discovery-first, not chatty.
                </p>

                {/* AI Talent Matches — roles picked by Claude that fit your Atlas footprint */}
                <section className="mt-8" data-testid="talent-matches-section">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <div className="flex items-center gap-2">
                            <Sparkles className="w-4 h-4" color={BRANCH_META.business.color} />
                            <h2 className="font-display font-black text-2xl tracking-tighter text-slate-900">
                                Roles picked for you
                            </h2>
                        </div>
                        <button
                            onClick={() => loadTalent(true)}
                            disabled={talentBusy}
                            className="text-xs font-display font-bold text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
                            data-testid="talent-refresh"
                        >
                            <RefreshCw className={`w-3 h-3 ${talentBusy ? "animate-spin" : ""}`} /> Refresh
                        </button>
                    </div>
                    <p className="text-sm text-slate-500 mt-1">
                        AI-matched to your beacons and branch XP. Refreshed daily.
                    </p>

                    {!talent && (
                        <div className="mt-3 crystal rounded-2xl p-5 text-sm text-slate-500" data-testid="talent-loading">
                            Finding roles that fit you…
                        </div>
                    )}
                    {talent && talent.cold_start && (
                        <div className="mt-3 crystal rounded-2xl p-5 flex items-start gap-3" data-testid="talent-cold-start">
                            <Sparkles className="w-5 h-5 flex-shrink-0" color={BRANCH_META.arts.color} />
                            <div>
                                <div className="font-display font-bold text-slate-900">Light a beacon to unlock better matches.</div>
                                <p className="text-sm text-slate-600 mt-1">You haven't lit any beacons yet — the matches below are the newest listings. Pick a discipline first for sharper picks.</p>
                                <Link to="/atlas" className="arcade-btn mt-3 inline-flex" data-variant="primary">
                                    Open the Atlas <ArrowRight className="w-4 h-4" />
                                </Link>
                            </div>
                        </div>
                    )}
                    {talent && talent.matches && talent.matches.length === 0 && !talent.cold_start && (
                        <div className="mt-3 crystal rounded-2xl p-5 text-sm text-slate-500" data-testid="talent-empty">
                            No roles matched to your disciplines yet. Try lighting a beacon in a busier frontier — or check back after a few days as orgs post new listings.
                        </div>
                    )}
                    {talent && talent.matches && talent.matches.length > 0 && (
                        <div className="mt-3 grid gap-3" data-testid="talent-matches-list">
                            {talent.matches.map((m) => (
                                <Link
                                    to={`/jobs/${m.job_id}`}
                                    key={m.job_id}
                                    className="crystal rounded-2xl p-4 hover:shadow-xl transition-all group"
                                    data-testid={`talent-match-${m.job_id}`}
                                >
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0 flex-1">
                                            <div className="flex flex-wrap items-center gap-2">
                                                <span className="chip" style={{ background: BRANCH_META.business.soft, color: BRANCH_META.business.text }}>
                                                    <Briefcase className="w-3 h-3" /> {(m.role_type || "role").replace("_", " ")}
                                                </span>
                                                <ScoreBadge score={m.score} />
                                            </div>
                                            <div className="font-display font-bold text-lg text-slate-900 mt-2 group-hover:text-humanities transition-colors">
                                                {m.title}
                                            </div>
                                            <div className="mt-1 flex flex-wrap items-center gap-1.5 text-sm text-slate-600">
                                                <Building2 className="w-3.5 h-3.5" />
                                                <span className="font-display font-semibold">{m.org_name}</span>
                                                {m.org_verified && <VerifiedOrgBadge />}
                                                {m.location && <span className="text-slate-400">·</span>}
                                                {m.location && <span>{m.location}</span>}
                                            </div>
                                            <p className="text-sm text-slate-700 mt-2 italic" data-testid={`talent-why-${m.job_id}`}>
                                                <Sparkles className="inline w-3 h-3 mr-1" color={BRANCH_META.business.color} />
                                                {m.why_fit}
                                            </p>
                                        </div>
                                        <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-humanities transition-colors flex-shrink-0 mt-1" />
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}
                </section>

                <div className="mt-8 flex items-center gap-1.5 p-1.5 rounded-full crystal w-fit">
                    <button
                        onClick={() => setTab("inbox")}
                        className={`px-4 py-2 rounded-full text-sm font-display font-bold transition-all inline-flex items-center gap-2 ${tab === "inbox" ? "bg-slate-900 text-cream" : "text-slate-700 hover:bg-white/70"}`}
                        data-testid="tab-inbox"
                    >
                        <Mail className="w-4 h-4" /> Received
                        {unread > 0 && (
                            <span className={`ml-0.5 min-w-[20px] px-1.5 rounded-full text-[10px] font-black tracking-wide ${tab === "inbox" ? "bg-cream text-slate-900" : "bg-slate-900 text-cream"}`}>
                                {unread}
                            </span>
                        )}
                    </button>
                    <button
                        onClick={() => setTab("sent")}
                        className={`px-4 py-2 rounded-full text-sm font-display font-bold transition-all inline-flex items-center gap-2 ${tab === "sent" ? "bg-slate-900 text-cream" : "text-slate-700 hover:bg-white/70"}`}
                        data-testid="tab-sent"
                    >
                        <Send className="w-4 h-4" /> Sent
                    </button>
                </div>

                <div className="mt-6 space-y-3">
                    {notes && notes.length === 0 && (
                        <div className="crystal rounded-3xl p-10 text-center">
                            <Sparkles className="w-8 h-8 mx-auto text-slate-400" />
                            <h3 className="font-display font-bold text-xl tracking-tight text-slate-900 mt-3">
                                {tab === "inbox" ? "No notes yet." : "You haven't sent any notes yet."}
                            </h3>
                            <p className="text-slate-600 mt-1 max-w-md mx-auto">
                                {tab === "inbox"
                                    ? "When a peer sends you a note, it'll appear here. Complete microtasks to signal you're active — that draws peers in."
                                    : "Open your interests dashboard, switch to the Peers tab, and drop a note to someone who shares your disciplines."}
                            </p>
                            <Link
                                to="/interests"
                                className="arcade-btn mt-5 inline-flex"
                                data-variant="primary"
                                data-testid="inbox-goto-interests"
                            >
                                <Users className="w-4 h-4" /> Open dashboard
                            </Link>
                        </div>
                    )}
                    {(notes || []).map((n, i) => (
                        <motion.div
                            key={n.id}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.03 }}
                            className={`crystal rounded-2xl p-5 ${tab === "inbox" && !n.read ? "ring-2 ring-humanities/40" : ""}`}
                            data-testid={`note-${n.id}`}
                        >
                            <div className="flex items-start gap-3">
                                <div
                                    className="w-10 h-10 rounded-full flex items-center justify-center font-display font-bold text-white flex-shrink-0"
                                    style={{ background: `linear-gradient(135deg, ${BRANCH_META.arts.color}, ${BRANCH_META.humanities.color})` }}
                                >
                                    {(tab === "inbox" ? n.from_name : n.to_name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase()}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-display font-bold text-slate-900">
                                            {tab === "inbox" ? n.from_name : `To ${n.to_name || "Explorer"}`}
                                        </span>
                                        {tab === "inbox" && !n.read && (
                                            <span className="chip text-[10px]" style={{ background: BRANCH_META.humanities.soft, color: BRANCH_META.humanities.text, padding: "0.1rem 0.5rem" }}>
                                                NEW
                                            </span>
                                        )}
                                        <span className="text-xs text-slate-400 ml-auto">
                                            {new Date(n.created_at).toLocaleString(undefined, {
                                                month: "short",
                                                day: "numeric",
                                                hour: "2-digit",
                                                minute: "2-digit",
                                            })}
                                        </span>
                                    </div>
                                    <p className="text-slate-800 mt-2 leading-relaxed whitespace-pre-wrap">
                                        {n.message}
                                    </p>
                                    <div className="mt-3 flex items-center gap-3">
                                        {tab === "inbox" && n.from_username && (
                                            <Link
                                                to={`/u/${n.from_username}`}
                                                className="text-xs text-slate-500 hover:text-slate-900 font-display font-bold"
                                                data-testid={`note-view-sender-${n.id}`}
                                            >
                                                View their ledger →
                                            </Link>
                                        )}
                                        {tab === "inbox" && !n.read && (
                                            <button
                                                onClick={() => markRead(n.id)}
                                                className="text-xs text-slate-500 hover:text-slate-900 font-display font-bold inline-flex items-center gap-1 ml-auto"
                                                data-testid={`note-mark-read-${n.id}`}
                                            >
                                                <MailOpen className="w-3 h-3" /> Mark read
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function ScoreBadge({ score }) {
    const s = Math.max(0, Math.min(100, Number(score) || 0));
    const strong = s >= 80;
    const moderate = s >= 40;
    const style = strong
        ? { bg: "#DBF3E9", fg: "#136A4A", label: "Strong fit" }
        : moderate
        ? { bg: "#FFF3CC", fg: "#7A5B00", label: "Moderate fit" }
        : { bg: "#F1F5F9", fg: "#475569", label: "Some overlap" };
    return (
        <span
            className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-display font-black tracking-wide"
            style={{ background: style.bg, color: style.fg }}
            data-testid="talent-score-badge"
        >
            {style.label} · {s}
        </span>
    );
}
